// Chess rules on the client, via chessops (plan §4: chessops as the TS-native
// legality source; the shared engine.wasm can replace it later). Used to compute
// legal destinations for chessground, detect promotions, and flag check.

import { Chess } from "chessops/chess";
import { chessgroundDests } from "chessops/compat";
import { parseFen } from "chessops/fen";
import { parseSquare } from "chessops/util";
import type { Key } from "chessground/types";
import type { Color } from "./types.ts";

/** Parse a FEN into a chessops position, or `undefined` if it doesn't parse. */
export function parsePosition(fen: string): Chess | undefined {
  return parseFen(fen).unwrap(
    (setup) => Chess.fromSetup(setup).unwrap((pos) => pos, () => undefined),
    () => undefined,
  );
}

/** Legal destinations per square, in chessground's `Dests` shape. */
export function legalDests(pos: Chess): Map<Key, Key[]> {
  return chessgroundDests(pos) as Map<Key, Key[]>;
}

/** Whether the given move is a pawn reaching the last rank (needs a promotion). */
export function isPromotion(pos: Chess, orig: Key, dest: Key): boolean {
  const from = parseSquare(orig);
  if (from === undefined) return false;
  const role = pos.board.getRole(from);
  if (role !== "pawn") return false;
  const destRank = dest[1];
  return destRank === "8" || destRank === "1";
}

/** The colour currently in check, if any (for chessground's check highlight). */
export function checkColor(pos: Chess): Color | undefined {
  return pos.ctx().checkers.nonEmpty() ? (pos.turn as Color) : undefined;
}

/** Split a UCI move into its `[from, to]` squares for a last-move highlight. */
export function uciSquares(uci: string): [Key, Key] {
  return [uci.slice(0, 2) as Key, uci.slice(2, 4) as Key];
}
